import { getFontEmbedCSS, toCanvas } from "html-to-image";
import { inlineComputedSvgStyles } from "../../../utils/svgStyles";

const CAPTURE_PIXEL_RATIO = 2;

// "bottom_view_plain": the same bottom view without its dimension lines, for the support-reaction page.
export type FinalReportImageKind = "bottom_view" | "bottom_view_plain" | "top_view" | "side_view" | "slope_view";

// Kept as a canvas: the bottom view may still be rotated (see orientCanvasForFullPage), and each one is
// encoded once, only when it goes into the report (see encodeCanvasAsPngBase64).
export type CapturedPreviewImage = {
  kind: FinalReportImageKind;
  canvas: HTMLCanvasElement;
};

// html-to-image captures the DOM as it is currently painted - right after they mount, the
// capture-stage copies (see DeckDesigner.module.css .captureStage) can still be mid-settle for a few
// more render passes (their own geometry - board segments, shape coordinates - commits via a
// watched-form-value effect, not synchronously with the values arriving), so waiting a single fixed
// frame isn't reliably enough. This instead polls until each target's rendered markup stops changing
// across two consecutive frames, capped so a genuinely still-changing element can't hang the export
// indefinitely - this is what let a freshly-opened calculation export a half-settled drawing that
// only looked right after a full page reload gave it more time to catch up. All targets are polled in
// the same frames, as they all mounted together.
const MAX_STABLE_CONTENT_FRAMES = 15;

export const waitForStableContent = (elements: readonly HTMLElement[]): Promise<void> =>
  new Promise((resolve) => {
    const previousHtml: (string | null)[] = elements.map(() => null);
    const stableStreaks = elements.map(() => 0);
    let framesChecked = 0;

    const check = () => {
      framesChecked += 1;

      elements.forEach((element, index) => {
        const currentHtml = element.innerHTML;

        if (currentHtml === previousHtml[index]) {
          stableStreaks[index] = (stableStreaks[index] ?? 0) + 1;
        } else {
          stableStreaks[index] = 0;
          previousHtml[index] = currentHtml;
        }
      });

      if (stableStreaks.every((stableStreak) => stableStreak >= 2) || framesChecked >= MAX_STABLE_CONTENT_FRAMES) {
        resolve();
        return;
      }

      requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
  });

// The web fonts' CSS html-to-image embeds in each capture, resolved once for all of them from their
// common container - otherwise each capture parses the style sheets and walks its own tree again. A
// superset of what each capture finds on its own: a font face a capture doesn't use changes nothing in it.
export const getCaptureFontEmbedCSS = (container: HTMLElement): Promise<string> => getFontEmbedCSS(container);

export const capturePreviewImage = async (
  kind: FinalReportImageKind,
  element: HTMLElement,
  fontEmbedCSS?: string,
): Promise<CapturedPreviewImage> => {
  const restoreStyles = inlineComputedSvgStyles(element);

  try {
    const canvas = await toCanvas(element, {
      backgroundColor: "#ffffff",
      pixelRatio: CAPTURE_PIXEL_RATIO,
      fontEmbedCSS,
    });

    return { kind, canvas };
  } finally {
    restoreStyles();
  }
};
